<template>
  <ToolPageLayout
    title="财务总监智擎工作台-创智物联"
    subtitle="月度经营报告 · 7+3滚动预测 · What-if模拟 · 数据刷新: 2026-06-16 08:42"
    :period="'报告周期：' + periodLabel + '（月结完成）'"
    tag="月结已锁定 · SAP CO-FI核对一致"
  >
    <div v-if="loading" class="sc loading-state">
      <i class="el-icon-loading"></i>
      <span>数据加载中...</span>
    </div>

    <div v-else-if="!rows.length" class="sc empty-data-state">
      <div class="empty-icon">📊</div>
      <div class="empty-title">暂无损益表数据</div>
      <div class="empty-desc">接口返回数据为空，已跳过空数据渲染</div>
    </div>

    <div v-else class="sc pl-statement-card">
      <div class="sc-header">
        <div class="sc-title">
          E2E损益表
          <span class="tag tag-red">预算vs实际vs同期</span>
          <span class="tag tag-purple">全年展望</span>
        </div>
        <div class="header-meta">
          <span class="threshold-text">
            差异阈值: 收入±{{ thresholds.revenue_pct }}% | 毛利±{{ thresholds.gp_pct }}% | 费用±{{ thresholds.opex_pct }}%
          </span>
          <span class="sc-badge tag-red">收入差距{{ fmtSignedWan(summary.revenueDiff) }}万</span>
          <span class="sc-badge profit-gap">利润缺口{{ fmtSignedWan(summary.profitGap) }}万</span>
        </div>
      </div>

      <div class="month-switcher">
        <button
          v-for="m in months"
          :key="m.periodKey"
          type="button"
          :class="['month-btn', { active: activePeriodKey === m.periodKey }]"
          @click="switchMonth(m.periodKey)"
        >{{ m.label }}</button>
      </div>

      <div class="report-table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th v-for="(col, ci) in columns" :key="ci" :class="colHeaderClass(ci)">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rows"
              :key="i"
              :class="rowClass(row)"
            >
              <template v-if="row.is_section">
                <td :colspan="columns.length">▶ {{ row.account_name }}</td>
              </template>
              <template v-else>
                <td :class="nameCellClass(row)">
                  <span v-if="row.show_dot" class="dot-red"></span>{{ row.account_name }}
                </td>
                <td :class="valueClass(row, 'actual_wan')">{{ formatCell(row, 'actual_wan') }}</td>
                <td :class="valueClass(row, 'budget_wan')">{{ formatCell(row, 'budget_wan') }}</td>
                <td :class="varClass(row, 'diff_wan')">{{ formatDiff(row, 'diff_wan') }}</td>
                <td :class="varClass(row, 'diff_pct')">{{ formatDiffPct(row) }}</td>
                <td>{{ formatCell(row, 'prior_year_wan') }}</td>
                <td :class="valueClass(row, 'ytd_actual_wan')">{{ formatCell(row, 'ytd_actual_wan') }}</td>
                <td>{{ formatCell(row, 'ytd_budget_wan') }}</td>
                <td :class="varClass(row, 'ytd_diff_wan')">{{ formatDiff(row, 'ytd_diff_wan') }}</td>
                <td :class="fyClass(row)">{{ formatCell(row, 'full_year_forecast_wan') }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="attribution.length" class="variance-attribution">
        <div class="attr-card attr-main">
          <span class="attr-main-title">收入差异归因分解</span>
          <span class="main-diff">{{ fmtSignedWan(attributionTotal) }}<span class="main-unit"> 万元</span></span>
          <span class="main-eq">=</span>
          <div v-for="(item, idx) in attribution" :key="'eq-' + idx" class="attr-eq-group">
            <span v-if="idx > 0" class="main-eq">+</span>
            <div class="attr-eq-item">
              <div :class="['attr-val', attrColorClass(item.attr_name)]">{{ fmtSignedWan(item.impact_wan) }}<span class="attr-unit">万</span></div>
              <div class="attr-label">{{ item.attr_name }}</div>
            </div>
          </div>
        </div>
        <div v-for="(item, i) in attribution" :key="'detail-' + i" class="attr-card">
          <div class="attr-label">{{ item.attr_name }}驱动因素</div>
          <div :class="['attr-val', attrColorClass(item.attr_name)]">{{ fmtSignedWan(item.impact_wan) }}万</div>
          <div class="attr-sub">{{ item.breakdown || `占收入差的 ${(item.impact_pct * 100).toFixed(1)}%` }}</div>
          <div class="attr-breakdown">{{ item.description }}</div>
        </div>
      </div>

      <div class="annot-footnote">
        <b>ⓘ 全年展望</b>：全年收入预测48.7亿，较年初预算50.4亿下调3.3%。核心风险：<b class="text-red">BOM物料涨价</b>（PCB+8.2%/铜+5.4%/锡+4.1%，对应毛利影响-412万/月）+ <b class="text-amber">采购降本兑现率</b>（当前60% vs 目标100%，年度缺口328万）。四项关差动作预计追回668万/月，可将毛利率从14.5%拉回16.0%目标——<b class="text-red">下半年需重点推进替代料验证与锁价谈判</b>。
      </div>
    </div>
  </ToolPageLayout>
</template>

<script>
import ToolPageLayout from './ToolPageLayout.vue'
import { fetchPlStatement, fetchRevenueAttribution, unwrapFpaResponse } from '@/api/fpa'
import { MOCK_PL_STATEMENT, MOCK_REVENUE_ATTRIBUTION } from './mockData'
import { fmtWan, fmtPct } from './formatters'

const DEFAULT_COLUMNS = [
  '科目（万元）', '当月实际', '当月预算', '当月差异', '差异率',
  '去年同期', 'YTD实际', 'YTD预算', 'YTD差异', '全年预测'
]

const DEFAULT_MONTHS = [
  { label: '5月', periodKey: 202605 },
  { label: '4月', periodKey: 202604 },
  { label: '3月', periodKey: 202603 },
  { label: '2月', periodKey: 202602 },
  { label: '1月', periodKey: 202601 }
]

const ATTR_COLOR = { '量差': 'red', '价差': 'amber', '结构差': 'purple' }

const SUBTOTAL_CODES = ['GP', 'GP_RATE', 'OPEX', 'OP', 'OP_RATE', 'NP']
const HIGHLIGHT_CODES = ['REV', 'COST', 'OP']

function parseMonths(availableMonths) {
  if (!availableMonths || !availableMonths.length) return []
  return availableMonths.map(m => {
    const match = String(m.period_label || '').match(/(\d+)月/)
    return {
      label: match ? `${match[1]}月` : m.period_label,
      periodKey: m.period_key
    }
  })
}

function mergeMonths(existing, incoming) {
  const map = new Map()
  ;[...(existing || []), ...parseMonths(incoming || [])].forEach(m => {
    if (m.periodKey) map.set(m.periodKey, m)
  })
  return Array.from(map.values()).sort((a, b) => b.periodKey - a.periodKey)
}

function parsePlStatementApi(data) {
  if (!data || !data.sections || !data.sections.length) return null

  const rows = []
  data.sections.forEach(section => {
    rows.push({ is_section: true, account_name: section.group })

    ;(section.rows || []).forEach(r => {
      const isRate = r.is_rate
      const isSub = r.is_sub || (r.indent || 0) > 0
      rows.push({
        account_code: r.account_code,
        account_name: r.account_name,
        is_section: false,
        indent: (r.indent || 0) > 0,
        indent_level: r.indent || 0,
        is_pct: isRate,
        is_pp: isRate,
        actual_wan: r.curr_actual,
        budget_wan: r.curr_budget,
        diff_wan: r.curr_diff,
        diff_pct: r.curr_diff_rate,
        prior_year_wan: r.curr_prior,
        ytd_actual_wan: r.ytd_actual,
        ytd_budget_wan: r.ytd_budget,
        ytd_diff_wan: r.ytd_diff,
        full_year_forecast_wan: r.fy_forecast,
        show_dot: HIGHLIGHT_CODES.includes(r.account_code),
        highlight: r.account_code === 'OP',
        is_subtotal: SUBTOTAL_CODES.includes(r.account_code),
        invert_variance: section.group === '成本' && !isRate && !['GP', 'GP_RATE'].includes(r.account_code),
        expense_bad: section.group === '费用' && !isRate && r.account_code !== 'OPEX_SALES_RATE',
        fy_muted: isSub,
        fy_bold: SUBTOTAL_CODES.includes(r.account_code) && (r.indent || 0) === 0
      })
    })
  })

  return {
    columns: data.columns || DEFAULT_COLUMNS,
    period_key: data.period_key,
    period_label: data.period_label || '2026年5月',
    available_months: data.available_months,
    summary: {
      revenueDiff: data.summary?.revenue_gap ?? 0,
      profitGap: data.summary?.profit_gap ?? 0
    },
    thresholds: {
      revenue_pct: data.thresholds?.revenue_pct ?? 2,
      gp_pct: data.thresholds?.gp_pct ?? 5,
      opex_pct: data.thresholds?.opex_pct ?? 10
    },
    rows
  }
}

function normalizeLegacyPlRows(raw) {
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : [])
  if (!list.length) return null

  const result = []
  let lastGroup = null
  list.forEach(item => {
    if (item.is_section) {
      result.push({ account_name: item.account_name, is_section: true })
      lastGroup = item.account_group || item.account_name
      return
    }
    const group = item.account_group
    if (group && group !== lastGroup && !item.account_name.startsWith('其中')) {
      result.push({ account_name: group, is_section: true })
      lastGroup = group
    }
    const ytdDiff = item.ytd_diff_wan != null
      ? item.ytd_diff_wan
      : (item.ytd_actual_wan != null && item.ytd_budget_wan != null
        ? item.ytd_actual_wan - item.ytd_budget_wan
        : null)
    result.push({
      ...item,
      ytd_diff_wan: ytdDiff,
      is_pct: item.is_pct || String(item.account_name || '').includes('率') || String(item.account_name || '').includes('比'),
      is_pp: item.is_pp || ['毛利率', '经营利润率', '占收入比'].includes(item.account_name),
      indent: item.indent || String(item.account_name || '').startsWith('其中'),
      show_dot: item.show_dot || item.highlight || item.account_code === 'REV' || item.account_code === 'COGS',
      invert_variance: item.invert_variance || item.account_group === '成本',
      expense_bad: item.expense_bad || item.account_group === '费用'
    })
  })
  return result.length ? result : null
}

export default {
  name: 'ToolFpaPlStatement',
  components: { ToolPageLayout },
  data() {
    return {
      loading: true,
      columns: DEFAULT_COLUMNS,
      rows: [],
      attribution: [],
      months: DEFAULT_MONTHS,
      activePeriodKey: 202605,
      periodLabel: '2026年5月',
      summary: { revenueDiff: 0, profitGap: 0 },
      thresholds: { revenue_pct: 2, gp_pct: 5, opex_pct: 10 }
    }
  },
  computed: {
    attributionTotal() {
      return this.attribution.reduce((s, item) => s + (item.impact_wan || 0), 0)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    fmtSignedWan(val) {
      if (val == null || Number.isNaN(Number(val))) return '—'
      const n = Number(val)
      const sign = n > 0 ? '+' : n < 0 ? '-' : ''
      return sign + fmtWan(Math.abs(n))
    },
    attrColorClass(name) {
      return ATTR_COLOR[name] || 'red'
    },
    switchMonth(periodKey) {
      if (this.activePeriodKey === periodKey) return
      this.activePeriodKey = periodKey
      this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const [plRes, attrRes] = await Promise.all([
          fetchPlStatement(this.activePeriodKey),
          fetchRevenueAttribution(this.activePeriodKey)
        ])
        const plRaw = unwrapFpaResponse(plRes)
        const attrRaw = unwrapFpaResponse(attrRes)
        const parsed = parsePlStatementApi(plRaw)

        if (parsed && parsed.rows.length) {
          this.applyParsed(parsed)
        } else {
          const legacyRows = normalizeLegacyPlRows(plRaw)
          if (legacyRows) {
            this.columns = DEFAULT_COLUMNS
            this.rows = legacyRows
            this.periodLabel = '2026年5月'
          } else {
            this.columns = DEFAULT_COLUMNS
            this.rows = MOCK_PL_STATEMENT
            this.periodLabel = '2026年5月'
            this.summary = { revenueDiff: -1400, profitGap: -3713 }
          }
        }

        this.attribution = Array.isArray(attrRaw) && attrRaw.length ? attrRaw : MOCK_REVENUE_ATTRIBUTION
      } catch (e) {
        this.columns = DEFAULT_COLUMNS
        this.rows = MOCK_PL_STATEMENT
        this.periodLabel = '2026年5月'
        this.summary = { revenueDiff: -1400, profitGap: -3713 }
        this.attribution = MOCK_REVENUE_ATTRIBUTION
      } finally {
        this.loading = false
      }
    },
    applyParsed(parsed) {
      this.columns = parsed.columns
      this.rows = parsed.rows
      this.summary = parsed.summary
      this.thresholds = parsed.thresholds
      this.periodLabel = parsed.period_label
      this.activePeriodKey = parsed.period_key || this.activePeriodKey
      if (parsed.available_months && parsed.available_months.length) {
        const merged = mergeMonths(this.months, parsed.available_months)
        this.months = merged.length ? merged : this.months
      }
    },
    colHeaderClass(index) {
      const map = {
        1: 'col-actual',
        2: 'col-budget',
        3: 'col-variance',
        4: 'col-variance',
        5: 'col-prior',
        6: 'col-ytd-a',
        7: 'col-ytd-b',
        8: 'col-ytd-v',
        9: 'col-fy-fc'
      }
      return map[index] || ''
    },
    nameCellClass(row) {
      return {
        indent: row.indent_level === 1,
        indent2: row.indent_level >= 2
      }
    },
    rowClass(row) {
      if (row.is_section) return 'row-section'
      if (row.is_subtotal) return row.highlight ? 'row-subtotal highlight' : 'row-subtotal'
      if (row.highlight) return 'highlight'
      return ''
    },
    formatCell(row, key) {
      const val = row[key]
      if (val == null) return '—'
      if (row.is_pct) return fmtPct(val)
      return fmtWan(val)
    },
    formatDiff(row, key) {
      const val = row[key]
      if (val == null) return '—'
      if (row.is_pp) {
        const sign = val > 0 ? '+' : ''
        return `${sign}${Number(val).toFixed(2)}pp`
      }
      const sign = val > 0 ? '+' : ''
      return sign + fmtWan(val)
    },
    formatDiffPct(row) {
      if (row.is_pp || row.is_pct) return '—'
      if (row.diff_pct == null) return '—'
      const sign = row.diff_pct > 0 ? '+' : ''
      return `${sign}${Number(row.diff_pct).toFixed(1)}%`
    },
    varClass(row, key) {
      const val = row[key]
      if (val == null || row.is_section) return ''
      if (val === 0) return ''
      const invert = row.invert_variance
      const expense = row.expense_bad
      let isGood
      if (expense) isGood = val < 0
      else if (invert) isGood = val < 0
      else isGood = val > 0
      return isGood ? 'var-pos' : 'var-neg'
    },
    valueClass(row, key) {
      if (!row.is_pct) return ''
      const val = row[key]
      const budget = row.budget_wan
      if (val == null || budget == null) return ''
      if (row.expense_bad) return val > budget ? 'var-neg' : ''
      return val < budget ? 'var-neg' : val > budget ? 'var-pos' : ''
    },
    fyClass(row) {
      if (row.fy_bold) return 'fy-bold'
      if (row.fy_muted) return 'fy-muted'
      return 'fy-bold'
    }
  }
}
</script>

<style lang="scss" scoped>
@import './tool-page.scss';

.pl-statement-card {
  .header-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .threshold-text {
    font-size: 10px;
    color: var(--muted);
  }
  .profit-gap {
    background: rgba(229, 75, 61, 0.1);
    color: var(--red);
  }
  .month-switcher {
    display: flex;
    gap: 4px;
    margin-bottom: 12px;
    padding: 4px;
    background: #f1f5f9;
    border-radius: 8px;
    width: fit-content;
  }
  .month-btn {
    padding: 4px 16px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    background: transparent;
    color: #64748b;
    transition: all 0.15s;
    &.active {
      background: #fff;
      color: #1e293b;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
  }

  .report-table {
    min-width: 960px;
    thead th.col-actual { color: var(--blue); }
    thead th.col-budget { color: var(--muted); }
    thead th.col-variance { color: var(--red); }
    thead th.col-prior { color: var(--muted); }
    thead th.col-ytd-a { color: var(--blue); }
    thead th.col-ytd-b { color: var(--muted); }
    thead th.col-ytd-v { color: var(--red); }
    thead th.col-fy-fc { color: var(--purple); font-weight: 700; }
  }
  .row-section td:first-child { color: var(--blue2); }
  .indent { padding-left: 24px !important; font-weight: 500 !important; }
  .indent2 { padding-left: 36px !important; font-weight: 500 !important; font-size: 10px; }
  .dot-red {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--red);
    margin-right: 4px;
    vertical-align: middle;
  }
  .fy-bold { color: var(--purple); font-weight: 700; }
  .fy-muted { color: var(--muted); }

  .variance-attribution {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed var(--line);
  }
  .attr-card {
    padding: 8px 10px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: var(--surface2);
    text-align: center;
  }
  .attr-card.attr-main {
    grid-column: 1 / -1;
    background: linear-gradient(135deg, rgba(229, 75, 61, 0.03), #fff);
    border-color: rgba(229, 75, 61, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 16px;
    text-align: left;
  }
  .attr-main-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text);
  }
  .main-diff {
    font-size: 18px;
    font-weight: 800;
    color: var(--red);
  }
  .main-unit { font-size: 12px; font-weight: 500; }
  .main-eq { font-size: 12px; color: var(--muted); }
  .attr-eq-group { display: flex; align-items: center; gap: 10px; }
  .attr-eq-item { text-align: center; }
  .attr-unit { font-size: 10px; font-weight: 500; }
  .attr-label {
    font-size: 9px;
    color: var(--muted);
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .attr-val {
    font-size: 16px;
    font-weight: 800;
    &.red { color: var(--red); }
    &.amber { color: var(--amber); }
    &.purple { color: var(--purple); }
  }
  .attr-sub {
    font-size: 8px;
    color: var(--muted);
    margin-top: 2px;
  }
  .attr-breakdown {
    font-size: 8px;
    color: var(--muted);
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px dashed var(--border);
    line-height: 1.5;
    text-align: left;
  }
  .annot-footnote {
    .text-red { color: var(--red); }
    .text-amber { color: var(--amber); }
  }
  .loading-state {
    padding: 40px;
    text-align: center;
    font-size: 12px;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    .el-icon-loading {
      font-size: 18px;
      color: var(--blue);
    }
  }
  .empty-data-state {
    padding: 60px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    .empty-icon { font-size: 40px; }
    .empty-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--text);
    }
    .empty-desc {
      font-size: 12px;
      color: var(--muted);
    }
  }
}
</style>
